import React from 'react';

export const GMV_KEY = 'gmv';
export const MAM_KEY = 'mam';
export const RAW_KEY = 'raw';
export const NOT_SENT_KEY = 'notSent';
export const LAST_UPDATED_METRICS_KEY = 'lastUpdated';
export const DROPPED_BY_XCO_KEY = 'droppedByXCO';
export const REPORTS_KEY = 'reports';
export const CAMPAIGN_TYPE_KEY = 'campaignType';
export const DEFAULT_VERTICAL = 'food';
export const colorPaletteList = ['#e71d36', '#264653', '#2a9d8f', '#e9c46a', '#f4a261', '#e76f51', '#f46036', '#1b998b', '#c5d86d', '#754668', '#587d71', '#4daa57', '#b5dda4', '#f9eccc'];
export const labelMetricTypeMapping = {
  [MAM_KEY]: {
    sectionTitle: 'MAM Metrics',
    sectionInfo: 'MAM Metrics show the amount of GMV, orders that are attributed to the comms your campaign sent. Learn more about MAM metrics',
  },
  [RAW_KEY]: {
    sectionTitle: 'Raw Business Metrics',
    sectionInfo: 'Raw business metrics records users transactions without any attribution logic. Learn more about raw business metrics',
  },
};
export const headerTextInfoMapping = {
  vertical: 'Business Vertical',
  metrics: 'Metrics',
  hereLink: 'https://wiki.grab.com/display/REW/Campaign+Performance+Metrics',
};
export const ControlGroupName = 'Control Group';
export const ControlBranchID = 'Control';
export const BizMetricSignificanceThreshold = 95;

export const SCORE_CATEGORY = {
  good: 'Good',
  great: 'Great',
  bad: 'Bad',
};

export const SCORE_COLOR = {
  [SCORE_CATEGORY.great]: '#00B14F',
  [SCORE_CATEGORY.good]: '#136FD8',
  [SCORE_CATEGORY.bad]: '#F09800',
};

export const RATE_NAME_DISPLAY = {
  openRate: 'Open Rate',
  clickThroughRate: 'CTR',
};

type FAQItem = {
  faqID: string;
  title: string;
  content: React.ReactElement;
};

export const FAQ_PERFORMANCE_LIST: FAQItem[] = [
  {
    faqID: '1',
    title: 'How should I read categories: "great", "good", and "improvement needed"?',
    content: (
      <ol>
        <li>Great refers to if the metric is greater than the 90th percentile of the benchmark campaigns (green font)</li>
        <li>Good refers to if the metric is greater than the average of the benchmark campaigns but less than the 90th percentile (blue font)</li>
        <li>Improvement needed refers to if the metric is below the average of the benchmark campaigns (orange font)</li>
      </ol>
    ),
  },
  {
    faqID: '2',
    title: 'What is/are the criteria for a campaign to be benchmarked?',
    content: (
      <div>
        <p>For each campaign, benchmarking will be done for the period between the campaign start date and two weeks after the campaign end date.</p>
        <p>For example, if Campaign A starts on 6 May 2023 and ends on 13 May 2023, benchmarking will be done for the period between 6 May 2023 and 27 May 2023. This means that during this period, benchmark information with regard to Campaign A&apos;s performance relative to other similar campaigns will be updated daily and displayed on Holos. From 28 May 2023 onwards, the benchmark results that will be shown on Holos for Campaign A will be the latest information as of 27 May 2023.</p>
      </div>
    ),
  },
  {
    faqID: '3',
    title: 'How is benchmarking done for each campaign?',
    content: (
      <div>
        <p>A campaign that is within the benchmark period will be compared against all campaigns whose campaign start dates are within the benchmark period, where the benchmark period is defined as:</p>
        <ol type="i">
          <li>Benchmark start date: 14 weeks before the campaign start date (comprising 2 weeks of buffer and 12 weeks of look-back period)</li>
          <li>Benchmark end date: 2 weeks before the campaign start date</li>
        </ol>
        <p>For example, if Campaign A starts on 6 May 2023 and meets the criteria to be benchmarked, it will be compared against similar campaigns which started between 28 Jan 2023 and 22 Apr 2023.</p>
        <p>The rationale of the 2 weeks buffer is to prevent an unfair comparison of a campaign against another campaign which may be new.</p>
      </div>
    ),
  },
  {
    faqID: '4',
    title: 'How do you define "similar campaigns"?',
    content: (
      <div>
        <p>Your campaign is measured against other campaigns who share similar attributes. These attributes are channel, segment size, campaign objective, vertical, country, and target user type.</p>
        <p>In determining similarity, not all attributes of other campaigns need to be the same as the attributes of your campaign. Instead, campaign similarity is computed based on multi-dimensional cosine similarity scores. For each campaign, only the top 5% of most similar campaigns in terms of cosine similarity scores will be considered in calculating the performance benchmarks.</p>
      </div>
    ),
  },
  {
    faqID: '5',
    title: 'What is the sample size of the benchmark campaigns?',
    content: (
      <p>Benchmarking may not be most meaningful if there are too few similar campaigns to benchmark against. Hence, the minimum sample size required to calculate performance benchmarks is currently set at 300. Benchmarking will not be done if the number of most similar campaigns does not meet this threshold.</p>
    ),
  },
  {
    faqID: '6',
    title: 'What does PP mean?',
    content: (
      <p>Percentage Point (PP) illustrates the absolute difference between the benchmark % and your campaign&apos;s metric % (i.e., if the average benchmark for open rate is 10% and your campaign open rate is 7%, you need to improve by 3PP to reach the average open rate)</p>
    ),
  },
  {
    faqID: '7',
    title: 'How can I use these benchmarks to improve future campaigns?',
    content: (
      <p>Benchmarks aim to help you evaluate how effective your campaign is in engaging your audience against other similar campaigns. If your metric shows that you need improvement, you could consider adjusting variables such as: content, user segments, channel. Please refer to <a target={'_blank'} href={'https://docs.google.com/presentation/d/11no0W71bjcBb1z1ZsbzAaK9YG7gtZKfIXnZcm0eK1rc/edit#slide=id.g13a4b505d63_1_50'}>PAX Comms Guidelines.</a></p>
    ),
  },
  {
    faqID: '8',
    title: 'Can I refer to some of the benchmark campaigns to view their set up?',
    content: (
      <p>We are working on allowing you to view top campaigns used for benchmarking, and provide recommendations on how to improve to achieve a similar performance.</p>
    ),
  },
  {
    faqID: '9',
    title: 'Why is there no benchmark available?',
    content: (
      <p>To ensure the benchmark is reliable and unbiased, we only show benchmarks when there are enough most similar campaigns to generate.</p>
    ),
  },
];
